import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TrendsAdvancedParams {
  occupation_codes: string[];
  area: string;
  start_year?: number;
  end_year?: number;
  include_projections?: boolean;
}

interface TrendAnalysis {
  occupation_code: string;
  area_code: string;
  industry_code?: string;
  year: number;
  growth_5yr: number;
  growth_10yr: number;
  growth_total: number;
  volatility_index: number;
  recession_impact_score: number;
  peak_year: number;
  trough_year: number;
  recovery_periods: string;
}

interface WageHistory {
  year: number;
  occ_code: string;
  occ_title: string;
  a_mean: number;
  employment: number;
  a_p10?: number;
  a_p25?: number;
  a_median?: number;
  a_p75?: number;
  a_p90?: number;
}

interface EconomicContext {
  year: number;
  area_code: string;
  unemployment_rate?: number;
  gdp_growth?: number;
  recession_flag: boolean;
  recovery_phase?: string;
  industry_performance_index?: number;
}

interface TrendsAdvancedData {
  trends_analysis: TrendAnalysis[];
  wage_history: WageHistory[];
  economic_context: EconomicContext[];
  meta: {
    area: string;
    start_year: number;
    end_year: number;
    is_pro: boolean;
    occupation_count: number;
    data_years: number;
    includes_projections: boolean;
  };
}

export const useTrendsAdvanced = (params: TrendsAdvancedParams) => {
  const { user } = useAuth();

  return useQuery<TrendsAdvancedData>({
    queryKey: ['trends-advanced', params, user?.id],
    queryFn: async (): Promise<TrendsAdvancedData> => {
      const { data, error } = await supabase.functions.invoke('api-trends-advanced', {
        body: {
          ...params,
          user_id: user?.id
        }
      });

      if (error) {
        // Log error for debugging
        console.error('Error fetching advanced trends:', error);
        // Throw user-friendly error
        throw new Error(
          error.message || 'Unable to load trends data. Please try again later.'
        );
      }

      if (!data) {
        throw new Error('No data received from server. Please try again.');
      }

      return data;
    },
    enabled: params.occupation_codes.length > 0,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60 * 2, // 2 hours
  });
};